import React, { memo, useMemo } from "react";
import { Maximize2 } from "lucide-react";

export const A4_WIDTH = 794;
export const A4_HEIGHT = 1123;

const BOTTOM_MARGIN = 20;

export type A4PreviewShellProps = {
  children: React.ReactNode;
  /** 0–1, appliqué à la feuille (794×1123) */
  zoom: number;
  title?: string;
  onFullscreen?: () => void;
  /** Espace vertical sous le cadre (px) */
  marginBottom?: number;
  className?: string;
  /** Masquer l’en-tête (titre + bouton) — ex. modale plein écran */
  hideHeader?: boolean;
};

function A4PreviewShellInner({
  children,
  zoom,
  title = "Aperçu document",
  onFullscreen,
  marginBottom = BOTTOM_MARGIN,
  className = "",
  hideHeader = false,
}: A4PreviewShellProps): React.JSX.Element {
  const z = useMemo(() => Math.max(0.12, Math.min(1, zoom)), [zoom]);
  const boxW = A4_WIDTH * z;
  const boxH = A4_HEIGHT * z + marginBottom;

  return (
    <div className={`w-full min-w-0 ${className}`}>
      {!hideHeader && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            {title} · {Math.round(z * 100)} %
          </p>
          {onFullscreen ? (
            <button
              type="button"
              onClick={onFullscreen}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-600 bg-slate-800/80 px-2.5 py-1.5 text-[11px] font-medium text-slate-200 hover:bg-slate-700"
            >
              <Maximize2 className="h-3.5 w-3.5" />
              Aperçu plein écran
            </button>
          ) : null}
        </div>
      )}
      <div className="rounded-xl border border-slate-800/90 bg-slate-950/50 p-3 shadow-inner ring-1 ring-black/15 md:p-4">
        <div className="flex w-full justify-center overflow-x-hidden">
          <div
            className="relative overflow-hidden rounded-sm shadow-xl ring-1 ring-slate-300/70"
            style={{ width: boxW, height: boxH }}
          >
            <div
              className="absolute top-0 box-border bg-white p-8 text-slate-900 sm:p-10"
              style={{
                left: "50%",
                width: A4_WIDTH,
                minHeight: A4_HEIGHT,
                transform: `translateX(-50%) scale(${z})`,
                transformOrigin: "top center",
              }}
            >
              <div className="text-xs">{children}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const A4PreviewShell = memo(A4PreviewShellInner);
